import { useContext, useEffect, useMemo, useRef, useState } from "react"

import {
  AppointmentCountdownRibbon,
  CommentsModal,
  DataTable,
  DataTableSessionHeader,
  Dot,
  EllipsisDropdown, 
  EmptyPlaceholder,
  EventPageHeader, 
  EventViewsTracker,
  GroupBookingModal, 
  GroupBookingSlotsSelectorModal, 
  ManageAvailabilityModal,
  Page,
  StaffMeetingsModal,
} from "@components"

import { ContextualSidebarContext, NotificationContext, UserContext } from "@context"

import { 
  CREATE_BOOKING_SLOT_MUTATION, 
  DELETE_BOOKING_SLOT_MUTATION, 
  GET_GROUP_LESSONS,
  GET_STAFF_SCHEDULE_PDF_DOWNLOAD_LINK, 
  GET_STAFF_SCHEDULE_XLS_DOWNLOAD_LINK, 
  GET_STAFF_SCHEDULE, 
  UPDATE_ATTENDANCE_MUTATION, 
  UPDATE_SLOTS_STATUS 
} from "@graphql"

import { 
  downloadFile, 
  getAttendanceStatus, 
  getCurrentSession, 
  getGraphQLErrorMessage, 
  hasExpired, 
  isToday, 
  mySqlToLocal 
} from "@helpers"

import { 
  useColumnManager, 
  useSaveTeacherComments, 
  useSessionSelectorSidebar, 
  useSetCurrentEvent 
} from "@hooks"

import { useMutation, useLazyQuery, useQuery } from "@apollo/client"
import { Button, Chip, Spinner } from "@heroui/react"
import { ChatBubbleBottomCenterTextIcon, ChatBubbleOvalLeftEllipsisIcon } from "@heroicons/react/24/solid"
import { ChatBubbleBottomCenterIcon, ChatBubbleOvalLeftIcon, ClipboardDocumentListIcon } from "@heroicons/react/24/outline"
import { useParams } from "react-router-dom"
import { columns, defaultColumns, downloadOptions, filters, filterConditions } from "./data"
import { EventDetails, EventViews, LessonsStudents, SessionStats } from "./SidebarContent"
import Stats from "./Stats"

const StaffSchedule = () => {
  const eventId = parseInt(useParams().event, 10)
  const [processingRowId, setProcessingRowId] = useState(null)
  const { setContextualSidebar } = useContext(ContextualSidebarContext)
  const { user, isLoadingCurrentEvent } = useContext(UserContext)
  const [group, setGroup] = useState(null)
  const [lessons, setLessons] = useState([])
  const [location, setLocation] = useState(null)
  const [hideLoader, setHideLoader] = useState(false)
  const [highlightMeetingsButton, setHighlightMeetingsButton] = useState(false)
  const [isSavingBulkAvailability, setIsSavingBulkAvailability] = useState(false)
  const [isBookingSlot, setIsBookingSlot] = useState(false)
  const [isDownloadingStaffSchedulePDF, setIsDownloadingStaffSchedulePDF] = useState(false)
  const [isDownloadingStaffScheduleXls, setIsDownloadingStaffScheduleXls] = useState(false)
  const [refetching, setRefetching] = useState(false)
  const [schedule, setSchedule] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedSlotId, setSelectedSlotId] = useState(null)
  const [selectedFilter, setSelectedFilter] = useState([])
  const { notify } = useContext(NotificationContext)
  const [session, setSession] = useState(null)
  const [modals, setModals] = useState({
    parentComments: { show: false },
    teacherComments: { show: false },
    groupBooking: { show: false },
    groupBookingSlotsSelector: { show: false },
    manageAvailability: { show: false },
    meetings: { show: false },
  })
  
  const searchInputRef = useRef(null)

  const [pagination, setPagination] = useState({
    // return all records - ignore pagination - max slots currently for a teacher is 60 so shouldn't be an issue 
    staffSchedule: { page: 1, perPage: 2000 },
  })
    
  useSetCurrentEvent(eventId)
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "event.manage.staff-schedule.columns", defaultColumns)

  const { openSessionSelectorSidebar, closeSessionSelectorSidebar } = useSessionSelectorSidebar()
  const { getMode: getCommentsSaveMode, handleSave: handleSaveComments, isSaving: isSavingComments } = useSaveTeacherComments()
  const isReadOnlyMode = hasExpired(mySqlToLocal(session?.ends_at))

  /* eslint-disable react-hooks/exhaustive-deps */
  const tableActions = useMemo(() => {
    const actions = []

    if (user?.me?.client?.settings?.allow_teacher_availability) {
      actions.push({ id: 1, caption: "Manage Availability", section: "Actions", handleClick: () => handleShowModal("manageAvailability") })
    }

    if (user?.me?.client?.settings?.allow_teacher_bookings) {
      actions.push({ id: 2, caption: "Make Bookings", section: "Actions", handleClick: () => handleShowModal("groupBooking"), isDisabled: isReadOnlyMode })
    }

    actions.push({ id: 3, caption: "View Lessons / Students", section: "Actions", handleClick: () => setContextualSidebar({ open: true, title: "Lessons / Students", subTitle: "Your lessons and students", content: <LessonsStudents group={group} lessons={lessons} />, size: "lg" }) })

    if (user?.currentEvent) {
      actions.push({ id: 4, caption: "View Event Details", section: "Actions", handleClick: () => setContextualSidebar({ open: true, title: "Event Details", subTitle: "General details about the event", content: <EventDetails event={user.currentEvent} session={session} lessons={lessons} location={location} />, size: "lg" }) })
    }

    actions.push({ id: 5, caption: "Download Bookings PDF", "section": "Reports", handleClick: () => handleShowModal("groupBookingSlotsSelector") })
    actions.push({ id: 6, caption: "Download Schedule XLS", "section": "Reports", handleClick: () => handleGenerateStaffScheduleXlsDownload() })
    actions.push({ id: 7, caption: "Event Views", "section": "Stats", handleClick: () => setContextualSidebar({ open: true, title: "Event Views", subTitle: "See who has viewed this event", content: <EventViews event={user.currentEvent} user={user.me} />, size: "lg" }) })
    actions.push({ id: 8, caption: "Session Stats", "section": "Stats", handleClick: () => setContextualSidebar({ open: true, title: "Session Stats", subTitle: session.title, content: <SessionStats stats={getStats()} bookingCountsByDate={getBookingCountsByDate()} />, size: "lg" }) })

    return actions
  }, [
    isReadOnlyMode, 
    schedule, 
    session,
    setContextualSidebar,
    user?.currentEvent, 
    user?.me,
  ])

  const checkStartMeetingsNotification = (session) => {
    if (isToday(session?.starts_at) && !localStorage.getItem("event.manage.staff-schedule.meetings-notification-dismissed")) {
      setHighlightMeetingsButton(true)

      notify({ 
        title: "Meetings View", 
        type: "info", 
        content: "To view / start meetings, including video meetings, click the 'Start Meetings' button below", 
        open: true, 
        timeout: 0,
        onCloseCallBack: () => {
          localStorage.setItem("event.manage.staff-schedule.meetings-notification-dismissed", "true")
          setHighlightMeetingsButton(false)
        }
      })
    }
  }

  useEffect(() => {
    checkStartMeetingsNotification(session)
    const interval = setInterval(() => checkStartMeetingsNotification(session), 5 * 60 * 1000)
    return () => clearInterval(interval);
  }, [session]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSession(getCurrentSession(getUserSessions(user.currentEvent.sessions, user.me.id)))
  }, [user.currentEvent, user.me.id])

  useEffect(() => {
    const currentGroup = session?.groups?.find(group => group.user_id === user.me.id)
    
    if (currentGroup) {
      getGroupLessons({ variables: { id: currentGroup?.id } })
      setGroup(currentGroup)
    }
  }, [session, user.me.id])
  
  // Create Booking Slot
  const [createBookingSlot] = useMutation(CREATE_BOOKING_SLOT_MUTATION, {
    onCompleted: () => notify({ title: "Success", type: "success", content: "Slot booked successfully", open: true }),
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while booking the slot."), open: true })
  })

  // Delete Booking Slot
  const [deleteBookingSlot] = useMutation(DELETE_BOOKING_SLOT_MUTATION, {
    onError: errors => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while deleting the slot."), open: true })
  })
  
  const [getGroupLessons] = useLazyQuery(GET_GROUP_LESSONS, {
    onCompleted: (data) => {
      setLessons(data.group.lessons)
      setLocation(data?.group?.location || null)
    },
  })

  // Get Staff Schedule - Main Query
  const { data, loading, refetch: refetchSchedule } = useQuery(GET_STAFF_SCHEDULE, {
    variables: {
      staffScheduleInput: {
        session_id: session?.id,
      },
      staffSchedulePage: pagination.staffSchedule.page,
      staffSchedulePerPage: pagination.staffSchedule.perPage
    },
    fetchPolicy: "network-only", 
    notifyOnNetworkStatusChange: true,
    pollInterval: 900000, // 15 minutes
    skip: isLoadingCurrentEvent || !session,
    onCompleted: (response) => {
      const newSchedule = response.staffSchedule.data.map(slot => ({
        ...slot, 
        starts_at: mySqlToLocal(slot.starts_at), 
        ends_at: mySqlToLocal(slot.ends_at)
      }))

      setSchedule(newSchedule)
    },
    onError: errors => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while fetching the staff schedule."), open: true })
  })

  // Download Staff Schedule PDF
  const [getStaffSchedulePDFDownload] = useLazyQuery(GET_STAFF_SCHEDULE_PDF_DOWNLOAD_LINK, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      downloadFile(data.staffSchedulePDFDownload.filename, data.staffSchedulePDFDownload.url)
      setIsDownloadingStaffSchedulePDF(false)
    },
    onError: errors => {
      notify({ 
        title: "Error", 
        type: "danger", 
        content: "There was a problem downloading your Schedule. Please try again.", 
        open: true 
      })

      setIsDownloadingStaffSchedulePDF(false)
    }
  })

  // Download All Slots XLS
  const [getStaffScheduleXlsDownload] = useLazyQuery(GET_STAFF_SCHEDULE_XLS_DOWNLOAD_LINK, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      downloadFile(data.staffScheduleXlsDownload.filename, data.staffScheduleXlsDownload.url)
      setIsDownloadingStaffScheduleXls(false)
    },
    onError: errors => {
      notify({ 
        title: "Error", 
        type: "danger", 
        content: "There was a problem downloading your Schedule. Please try again.",
        open: true 
      })

      setIsDownloadingStaffScheduleXls(false)
    }
  })

  // Update Attendance
  const [updateAttendance] = useMutation(UPDATE_ATTENDANCE_MUTATION)

  // Update Slots Status
  const [updateSlotsStatus] = useMutation(UPDATE_SLOTS_STATUS, {
    onError: errors => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while updating slot status."), open: true }),

    onCompleted: (data) => {
      const newSchedule = schedule.map((schedule) => {
        const updatedSlot = data.updateSlotsStatus.updated.find((slot) => slot.id === schedule.id)
        
        if (updatedSlot) {
          return { ...schedule, slot_status: updatedSlot.status }
        }

        return schedule
      })

      setSchedule(newSchedule)
    }
  })

  const getBookingCountsByDate = () => {
    const countsByDate = schedule
      ?.filter(slot => slot.slot_status === "Booked")
      .reduce((bookingCountsByDate, slot) => {
        const bookingDate = slot.booking_slot_created_at.substr(0, 10)

        if (bookingCountsByDate[bookingDate]) {
          bookingCountsByDate[bookingDate].no_of_booked_slots++
        } else {
          bookingCountsByDate[bookingDate] = {
            date: bookingDate,
            no_of_booked_slots: 1,
          }
        }

        return bookingCountsByDate
      }, {})

    return Object.values(countsByDate)
  }

  const getFilteredSchedule = (schedule) => {
    if (!schedule) return schedule

    const searchTermLower = searchTerm.toLowerCase()
    let filteredSchedule = [...schedule]

    if (searchTerm) {
      filteredSchedule = schedule.filter(slot => 
        slot?.student?.toLowerCase().includes(searchTermLower) || 
        slot?.parent?.toLowerCase().includes(searchTermLower) || 
        slot?.lesson?.toLowerCase().includes(searchTermLower)
      )
    }

    if (selectedFilter.length === 0) return filteredSchedule

    return filteredSchedule.filter(slot => filterConditions[selectedFilter](slot))
  }

  const getGroupBookingModalSlots = () => {
    return schedule.map(slot => ({
      id: slot.id,
      starts_at: slot.starts_at,
      status: slot.slot_status,
    }))
  }

  const getStats = () => {
    return schedule?.reduce((stats, slot) => {
      if (slot?.slot_status === "Available") stats.available++
      if (slot?.slot_status === "Booked") stats.booked++
      if (slot?.slot_status === "Disabled") stats.disabled++
      if (slot?.attendance_status === "Attended") stats.attended++
      if (slot?.attendance_status === "Absent") stats.absent++
      if (slot?.attendance_status === "Unknown" && slot?.slot_status === "Booked") stats.unknown++
      stats.total++

      return stats
    }, { absent: 0, attended: 0, available: 0, booked: 0, disabled: 0, total: 0, unknown: 0 })
  }
  
  const getStatusColor = (status, attendance) => {
    if (attendance === "Attended") return "attended"
    if (attendance === "Absent") return "absent"
    if (status === "Booked") return "booking"
    if (status === "Available") return "available"
    if (status === "Disabled") return "disabled"
    
    return ""
  }
  
  const getUserSessions = (sessions, userId) => {
    return sessions.filter(session =>
      session.groups?.some(group => group.user_id === userId)
    )
  }

  const makeSlotAvailable = (slotId) => {
    setSchedule((prevSchedule) => {
      const newSchedule = [...prevSchedule]
      const index = newSchedule.findIndex((schedule) => schedule.id === slotId)

      if (index !== -1) {
        newSchedule[index] = resetSlot(newSchedule[index], "Available")
      }
      
      return newSchedule
    })
  }
  
  const resetSlot = (slot, status) => (
    {...slot,
      attendance_status: "Pending",
      booked_by_user: null,
      booking_id: null,
      invitation_email_last_sent_date: null,
      lesson: null,
      meeting_type: null,
      parent: null,
      slot_status: status,
      student: null,
    }
  )
  
  const handleBookSlot = (slotId, studentId, userId, lessonId, meetingTypeId) => {
    setIsBookingSlot(true)

    return createBookingSlot({
      variables: {
        input: {
          event_id: eventId,
          lesson_id: lessonId,
          slot_id: slotId,
          student_id: studentId,
          user_id: userId,
          quantity: 1,
          meeting_type_id: meetingTypeId
        }
      }
    }).then((response) => {
      setIsBookingSlot(false)
      return response
    })
  }

  const handleCancelBooking = data => {
    setProcessingRowId(data.id)

    deleteBookingSlot({ variables: { input: { booking_id: data.booking_id, slot_id: data.id }} }).then((response) => {
      setProcessingRowId(null)
        
      if (!response.errors) {
        makeSlotAvailable(data.id)
      }
    })
  }
  
  const handleChangeSession = (newSession) => {
    setSession({ ...user.currentEvent.sessions.find(session => session.id === newSession.id)})
    closeSessionSelectorSidebar()
  }

  const handleClearAllFilters = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = "" // not working
      setSelectedFilter([])
    }

    setSearchTerm("")
  }

  const handleCloseGroupBookingModal = (hasMadeBookings) => {
    if (hasMadeBookings) {
      setHideLoader(true)
      refetchSchedule().finally(() => setHideLoader(false))
    }
    
    handleCloseModal("groupBooking")
    setSelectedSlotId(null)
  }

  const handleCloseModal = modal => setModals({ ...modals, [modal]: { show: false } })

  const handleCloseStaffMeetingsModal = () => {
    setSelectedSlotId(null)
    handleCloseModal("meetings")
  }

  const handleDisableSlot = data => {
    setProcessingRowId(data.id)
    handleUpdateSlotStatus("DISABLED", data).then(() => setProcessingRowId(null))
  }

  const handleEnableSlot = data => {
    setProcessingRowId(data.id)
    handleUpdateSlotStatus("AVAILABLE", data).then(() => setProcessingRowId(null))
  }

  const handleGenerateStaffSchedulePDFDownload = (slotIds) => {
    setIsDownloadingStaffSchedulePDF(true)
    getStaffSchedulePDFDownload({ variables: { input: { group_id: group?.id, slot_ids: slotIds } }})
  }

  const handleGenerateStaffScheduleXlsDownload = () => {
    setIsDownloadingStaffScheduleXls(true)
    getStaffScheduleXlsDownload({ variables: { input: { group_id: group?.id } }})
  }

  const handleRefetch = () => {
    setRefetching(true)
    refetchSchedule().then(() => setRefetching(false))
  }

  const handleSaveBulkAvailability = slots => {
    setIsSavingBulkAvailability(true)
    
    updateSlotsStatus({
      variables: {
        input: {
          session_id: session.id,
          slots: slots.map(slot => ({ id: slot.id, status: slot.status.toUpperCase() })),
        }
      }
    }).then((res) => {
      setIsSavingBulkAvailability(false)
      
      if (!res.errors) {
        handleCloseModal("manageAvailability")
      }
    })
  }

  const handleSearchChange = searchTerm => {
    setSearchTerm(searchTerm)
  }
  
  const handleSetPagination = (key, value) => setPagination({ ...pagination, [key]: { ...pagination[key], ...value } })

  const handleShowModal = modal => setModals({ ...modals, [modal]: { show: true } })
  
  const handleShowBookingModal = data => {
    setSelectedSlotId(data.id)
    handleShowModal("groupBooking")
  }
  
  const handleShowSessionsSidebar = () => {
    openSessionSelectorSidebar({ sessions: getUserSessions(user.currentEvent.sessions, user.me.id), handleChangeSession })
  }
  
  const handleStartMeeting = (slotId) => {
    setSelectedSlotId(slotId)
    handleShowModal("meetings")
  }
  
  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilter => {
    setSelectedFilter(selectedFilter.size > 0 ? [selectedFilter.currentKey] : [] )
  }

  const handleUpdateAttendance = (attendanceQuantity, slot) => {
    updateAttendance({
      variables: {
        input: {
          booking_slot_id: slot.booking_slot_id,
          attended_quantity: attendanceQuantity
        }
      }
    }).then(({ data, errors }) => {
      if (errors || !data?.updateAttendance) {
        return
      }

      const updatedSchedule = schedule.map((schedule) => { 
        const status = getAttendanceStatus(attendanceQuantity)

        if (schedule.id === slot.id) {
          return { ...schedule, attendance_status: status }
        }

        return schedule
      })

      setSchedule(updatedSchedule)
    })
  }

  const handleUpdateSlotStatus = (status, data) => {
    return updateSlotsStatus({
      variables: {
        input: {
          session_id: session.id,
          slots: [{
            id: data.id,
            status
          }],
        }
      }
    })
  }
  
  const handleUpdateTeacherComment = (slotId, teacherComment, callback = null) => {
    const modifiedSlot = schedule.find(slot => slot.id === slotId)
    const mode = getCommentsSaveMode(modifiedSlot?.teacher_comments, teacherComment)

    if (mode === "unchanged") {
      if (callback) callback()
      return
    }

    handleSaveComments(eventId, teacherComment, mode, modifiedSlot?.booking_slot_id, modifiedSlot?.teacher_comment_id)
      .then((response) => {
        setSchedule((prevSchedule) => {
          const newSchedule = [...prevSchedule]
          const index = newSchedule.findIndex((schedule) => schedule.id === slotId)
          const comment = mode === "delete" ? null : response.comment
          const commentId = mode === "delete" ? null : response.id

          if (index !== -1) {
            newSchedule[index] = { ...newSchedule[index], teacher_comment_id: commentId, teacher_comments: comment }
          }
    
          return newSchedule
        })

        if (callback) callback()
      })
  }

  const handleViewComments = (data, modalType) => {
    setSelectedSlotId(data.id)
    handleShowModal(modalType)
  }

  const renderCell = (data, column, isReadOnlyMode, isLive) => {  
    const bookingActions = user.me.client.settings.allow_teacher_bookings ? [
      { id: 1, caption: "Book Slot", handleClick: () => handleShowBookingModal(data), show: () => data.slot_status === "Available", isDisabled: isReadOnlyMode },
      { id: 2, caption: "Cancel Booking", handleClick: () => handleCancelBooking(data), show: () => data.slot_status === "Booked", isDisabled: isReadOnlyMode }
    ] : []
    
    const availabilityActions = user.me.client.settings.allow_teacher_availability ? [
      { id: 3, caption: "Disable Slot", handleClick: () => handleDisableSlot(data), show: () => data.slot_status === "Available", isDisabled: isReadOnlyMode },
      { id: 4, caption: "Enable Slot", handleClick: () => handleEnableSlot(data), show: () => data.slot_status === "Disabled", isDisabled: isReadOnlyMode }
    ] : []
  
    const generalActions = [
      { id: 5, caption: isLive ? "Start Meeting" : "View Meeting", handleClick: () => handleStartMeeting(data.id), show: () => data.slot_status === "Booked" },
      { id: 6, caption: data.teacher_comments ? "View Your Comment" : "Add Comment", handleClick: () => handleViewComments(data, "teacherComments"), show: () => data.slot_status === "Booked" },
      { id: 7, caption: "View Parent Comment", handleClick: () => handleViewComments(data, "parentComments"), show: () => data.parent_comments },
    ]

    const actions = [...bookingActions, ...availabilityActions, ...generalActions]

    const classMap = {
      booked: "bg-booking text-white",
      available: "bg-available text-white",
      disabled: "bg-disabled text-white",
    }
    
    if (column === "status") {
      return <Dot color={getStatusColor(data.slot_status, data.attendance_status)} />
    }

    if (column === "actions" && processingRowId !== data.id) {
      return <EllipsisDropdown items={actions.filter((action) => action.show())} />
    } 
    
    if (column === "actions" && processingRowId === data.id) {
      return <Spinner size="sm" classNames={{ "base": "py-1.5"}} />
    }

    if (column === "starts_at") {
      return data.starts_at.format("HH:mm")
    }

    if (column === "meeting_type") {
      return (
        <div className="flex items-center gap-2">
          {data.meeting_type === "Telephone" && <div className="w-24 py-1 font-semibold text-center text-white bg-orange-500 rounded-full">Phone</div>}
          {data.meeting_type === "Venue" && <div className="w-24 py-1 font-semibold text-center text-white bg-purple-500 rounded-full">Venue</div>}
          {data.meeting_type === "Video Call" && <div className="w-24 py-1 font-semibold text-center text-white bg-green-500 rounded-full">Video</div>}
        </div>
      )
    }

    if (column === "comments") {
      return (
        <div className="flex items-center gap-2">
          {data.teacher_comments ? <ChatBubbleBottomCenterTextIcon onClick={() => handleViewComments(data, "teacherComments")} className="text-indigo-500 cursor-pointer hover:text-indigo-600 w-7 h-7" /> : <ChatBubbleBottomCenterIcon className="text-gray-200 w-7 h-7" />}
          {data.parent_comments ? <ChatBubbleOvalLeftEllipsisIcon onClick={() => handleViewComments(data, "parentComments")} className="text-indigo-500 cursor-pointer hover:text-indigo-600 w-7 h-7" /> : <ChatBubbleOvalLeftIcon className="text-gray-200 w-7 h-7" />}
        </div>
      )
    }

    if (column === "slot_status") {
      return (
        <Chip size="sm" classNames={{ content: "w-24 text-center" }} className={classMap[data.slot_status.toLowerCase()]}>
          {data.slot_status}
        </Chip>
      )
    }

    return data[column]
  }
  
  const groupTitle = (schedule && schedule.length > 0) ? schedule[0].group : "Current Group" // change this
  const defaultMeetingType = user?.currentEvent?.meetingTypes && user?.currentEvent?.meetingTypes.length === 1 ? user?.currentEvent?.meetingTypes[0] : null
  const bookedSlots = schedule?.filter(slot => slot.slot_status === "Booked") || []
  const selectedSlot = schedule?.find(slot => slot.id === selectedSlotId) || null
  const stats = getStats()
  const isLoading = !hideLoader && (loading || isLoadingCurrentEvent || session === null)
  const filteredSchedule = getFilteredSchedule(schedule)
  const isElipsisLoading = isDownloadingStaffScheduleXls
  const isLive = isToday(session?.starts_at)
  const userSessions = getUserSessions(user.currentEvent.sessions, user.me.id)

  return (
    <EventViewsTracker eventId={eventId}>
      <Page>
        <EventPageHeader
          title="Your Schedule"
          icon={<ClipboardDocumentListIcon className="page-header-icon" />}
          event={user?.currentEvent}
        />
              
        <Stats 
          absent={stats?.absent || 0}
          attended={stats?.attended || 0}
          available={stats?.available || 0} 
          booked={stats?.booked || 0} 
          disabled={stats?.disabled || 0} 
          total={stats?.total || 0} 
        />
        
        {isToday(session?.starts_at) && (
          <AppointmentCountdownRibbon 
            classNames={{ container: "mb-3" }} 
            handleClick={(id) => handleStartMeeting(id)}
            schedule={schedule?.filter(schedule => schedule.slot_status === "Booked")} 
          />
        )}
        
        <DataTable
          actions={
            <div className="flex items-center gap-1">
              <Button 
                className={`hidden font-semibold text-white md:block ${highlightMeetingsButton ? 'animate-pulse-green' : ''}`} 
                color="success" 
                onPress={() => bookedSlots.length > 0 && handleStartMeeting(bookedSlots[0].id)} 
                isDisabled={isLoading || bookedSlots.length === 0}
              >
                {isLive ? "Start Meetings" : "View Meetings"}
              </Button>
              
              {user.me.client.settings.allow_teacher_bookings && !isReadOnlyMode && (
                <Button 
                  className="hidden font-semibold text-white lg:block" 
                  color="success" 
                  onPress={() => handleShowModal("groupBooking")}
                  isDisabled={isLoading} 
                >
                  Make Bookings
                </Button>
              )}

              <EllipsisDropdown items={tableActions} size="md" isLoading={isElipsisLoading}></EllipsisDropdown>
            </div>
          }
          columns={columns}
          currentPage={pagination.staffSchedule.page}
          data={filteredSchedule}
          disallowEmptyFilterSelection={false}
          downloadOptions={downloadOptions}
          filters={filters}
          filtersSelectionMode="single"
          handleRefetch={() => handleRefetch()}
          handlePageChange={page => handleSetPagination("getSchedule", { page })}
          handleRenderCell={(item, key) => renderCell(item, key, isReadOnlyMode, isLive)}
          handleSearchChange={searchTerm => handleSearchChange(searchTerm)}
          handleSearchClear={() => setSearchTerm("")}
          handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
          handleToggleFilter={selectedFilter => handleToggleFilter(selectedFilter)}
          loading={isLoading}
          isRefetching={refetching}
          noDataMessage={
            <EmptyPlaceholder classNames="mt-2" title="No Slots Found" description="No slots found! Try clearing any filters and searches.">
              <Button className="mt-3 font-semibold" onPress={handleClearAllFilters} color="secondary" radius="full">Clear search</Button>
            </EmptyPlaceholder>
          }
          paginatorInfo={data?.paginatorInfo}
          searchInputPlaceHolder="Search by student / guardian / lesson"
          searchInputRef={searchInputRef}
          searchTerm={searchTerm}
          selectedColumns={selectedColumns}
          selectedFilters={selectedFilter}
          header={<DataTableSessionHeader session={session} handleShowSessionsSidebar={handleShowSessionsSidebar} showChangeSessionButton={userSessions.length > 1} />}
          showColumnsButton={true}
          showDownloadButton={false}
          showFilterCount={true}
          showRefetchButton={true}
          tableClasses="staff-schedule-table"
        />
        
        {modals.groupBooking.show && (
          <GroupBookingModal
            eventId={eventId}
            groupId={group?.id}
            handleBookSlot={(slotId, studentId, userId, lessonId, meetingTypeId) => handleBookSlot(slotId, studentId, userId, lessonId, meetingTypeId)}
            isSaving={isBookingSlot}
            handleClose={(hasMadeBookings) => handleCloseGroupBookingModal(hasMadeBookings)}
            selectedSlotId={selectedSlotId}
            initialSlots={getGroupBookingModalSlots()}
            defaultMeetingType={defaultMeetingType}
            meetingTypes={user?.currentEvent?.meetingTypes || []}
            show={true}
            title={groupTitle}
          />
        )}

        {modals.manageAvailability.show && (
          <ManageAvailabilityModal 
            slots={schedule} 
            show={true} 
            handleClose={() => handleCloseModal("manageAvailability")} 
            handleSave={(slots) => handleSaveBulkAvailability(slots)} 
            title={session.title} 
            isReadOnly={isReadOnlyMode} 
            isSaving={isSavingBulkAvailability}
          />
        )}

        {modals.meetings.show && (
          <StaffMeetingsModal
            handleClose={() => handleCloseStaffMeetingsModal()}
            handleUpdateAttendance={(attendanceQuantity, slot) => handleUpdateAttendance(attendanceQuantity, slot)}
            handleUpdateTeacherComment={(slotId, comment) => handleUpdateTeacherComment(slotId, comment)}
            initialSlotId={selectedSlotId}
            schedule={bookedSlots}
            session={session}
            event={user.currentEvent}
          />
        )}

        {modals.parentComments.show && (
          <CommentsModal 
            comment={selectedSlot?.parent_comments}
            description="The comments below are talking points from the parent."
            handleClose={() => handleCloseModal("parentComments") } 
            isEditable={false}
            showCharacterCount={false}
            subTitle={`Comments from ${selectedSlot.parent} (${selectedSlot.student})`}
            title={<div className="flex items-center justify-between">
              <span>Parent Comment</span>
              <Chip classNames={{ "content": "font-semibold" }} color="secondary" size="lg" className="px-6 mr-8">{selectedSlot.starts_at.format("HH:mm")} - {selectedSlot.ends_at.format("HH:mm")}</Chip>
            </div>}
          />
        )}

        {modals.teacherComments.show && (
          <CommentsModal 
            comment={selectedSlot?.teacher_comments}
            description="Add your comments concerning this appointment. These comments are private and only visible to you."
            handleClose={() => handleCloseModal("teacherComments") } 
            handleSave={(comment, callback) => handleUpdateTeacherComment(selectedSlotId, comment, callback)}
            isSaving={isSavingComments} 
            isEditable={true}
            subTitle={`${selectedSlot.student} - Appointment Comments`}
            title={<div className="flex items-center justify-between">
              <span>Your Comment</span>
              <Chip classNames={{ "content": "font-semibold" }} color="secondary" size="lg" className="px-6 mr-8">{selectedSlot.starts_at.format("HH:mm")} - {selectedSlot.ends_at.format("HH:mm")}</Chip>
            </div>}
          />
        )}

        {modals.groupBookingSlotsSelector.show && (() => (
          <GroupBookingSlotsSelectorModal
            buttonCaption="Download"
            checkedSlotIds={bookedSlots.map(slot => slot.id)}
            description="Select the appointments you would like to download. You may wish to select specific slots to share with other teachers who wish to join a video conference."
            handleClose={() => handleCloseModal("groupBookingSlotsSelector")}
            handleOK={(slotIds) => handleGenerateStaffSchedulePDFDownload(slotIds)}
            isProcessing={isDownloadingStaffSchedulePDF}
            show={true}
            slots={bookedSlots}
            title={groupTitle}
          />
        ))()}
      </Page>
    </EventViewsTracker>
  )
}

export default StaffSchedule
